
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useOnboarding } from "@/hooks/use-onboarding";
import { SidebarTrigger, SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Menu, X, ArrowRight } from "lucide-react";

import { Link as RouterLink } from "react-router-dom";
import { SidebarTrialProgress } from "@/components/trial/SidebarTrialProgress";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Separator } from "@/components/ui/separator";
import { PrivatePageSkeleton } from "@/components/ui/private-page-skeleton";
import { HeaderTrialProgress } from "@/components/trial/HeaderTrialProgress";
import AppSidebar from "@/components/AppSidebar";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

interface NavigationProps {
  children: React.ReactNode;
}

// Public pages array - moved outside component to prevent recreation
const PUBLIC_PAGES = [
  '/science', 
  '/features', 
  '/pricing', 
  '/blog',
  '/hacks',
  '/contact',
  '/',
  '/compare',
  '/about',
  '/careers',
  '/privacy',
  '/help'
];

// Public navigation items
const navigation = [
  { name: 'Home', href: '/' },
  { name: 'Features', href: '/features' },
  { name: 'Science', href: '/science' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Blog', href: '/blog' },
];

// Help navigation items (only shown when logged in)
const helpNavigation = [
  { name: 'Help', href: '/help' },
];

function NavigationComponent({
  children
}: NavigationProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  const {
    user,
    loading,
    initialized,
    isLoggingOut
  } = useAuth();
  const { breadcrumbOverrides } = useBreadcrumb();
  const { hasCompletedOnboarding, loading: onboardingLoading } = useOnboarding();
  const location = useLocation();

  // Memoized public page check
  const isPublicPage = useMemo(() => 
    PUBLIC_PAGES.includes(location.pathname) || 
    location.pathname.startsWith('/compare/') || 
    location.pathname.startsWith('/blog/') ||
    location.pathname.startsWith('/help') ||
    location.pathname.startsWith('/hacks') ||
    location.pathname.startsWith('/integrations/'),
    [location.pathname]
  );

  // Memoized onboarding page check
  const isOnboardingPage = useMemo(() => 
    location.pathname === '/onboarding',
    [location.pathname]
  );

  // Memoized profile settings check
  const isProfileSettings = useMemo(() => 
    location.pathname === "/settings" || location.pathname === "/settings/profile",
    [location.pathname]
  );



  // Memoized availability settings check
  const isAvailabilitySettings = useMemo(() => 
    location.pathname === "/settings/availability",
    [location.pathname]
  );

  // Memoized background class decision
  const shouldUseGrayBackground = useMemo(() => 
    isProfileSettings || isAvailabilitySettings,
    [isProfileSettings, isAvailabilitySettings]
  );

  // Check if a navigation item is active
  const isActiveLink = useCallback((href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  }, [location.pathname]);

  const scrollToWaitlist = () => {
    const waitlistElement = document.getElementById('waitlist');
    if (waitlistElement) {
      waitlistElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Memoized auth buttons render function
  const renderAuthButtons = useCallback(() => (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" asChild>
        <RouterLink to="/login">Log in</RouterLink>
      </Button>
      <Button size="sm" asChild>
        <RouterLink to="/signup">Sign up</RouterLink>
      </Button>
    </div>
  ), []);

  // Add scroll listener to detect when user has scrolled down
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  // Only log on mount and when auth state changes significantly
  useEffect(() => {
    // Removed excessive auth state change logging
  }, [user, loading, initialized, onboardingLoading, hasCompletedOnboarding]);

  // Show loading indicator while auth state is loading - only for private pages
  // Don't show skeleton for signup/login pages even if auth is loading
  // Also show loading during auth state transitions (like logout) to prevent sidebar errors
  const isAuthTransitioning = loading || !initialized || onboardingLoading;
  const shouldShowLoading = isAuthTransitioning && !isPublicPage && location.pathname !== '/signup' && location.pathname !== '/login' && !isOnboardingPage;
  
  if (shouldShowLoading) {
    return (
      <div className="min-h-screen flex w-full">
        <main className="flex-1 pt-16">
          <PrivatePageSkeleton variant="dashboard" />
        </main>
      </div>
    );
  }

  // Special handling for onboarding page - show minimal layout without navbar
  if (isOnboardingPage) {
    return (
      <div className="min-h-screen flex w-full">
        <main className="flex-1">
          {children}
        </main>
      </div>
    );
  }

  // Always show the public navigation on public pages or when user is not logged in
  if (isPublicPage || !user) {
    return (
      <div className="bg-background">
        {/* Fixed header with dynamic background based on scroll position */}
        <header className={`fixed inset-x-0 top-0 z-50 ${
          scrolled ? 'bg-background/95 backdrop-blur-sm border-b border-border/40' : 'bg-white'
        }`}>
          <nav aria-label="Global" className="flex items-center max-w-7xl m-auto justify-between p-6 xl:px-2 xl:py-8">
            <div className="flex lg:flex-1">
              <RouterLink to="/" className="-m-1.5 p-1.5">
                <span className="sr-only">ChronoCal</span>
                <img
                  alt="ChronoCal Logo"
                  src="/assets/icons/branding/chronocal-logo.svg"
                  className="h-6 w-auto"
                />
              </RouterLink>
            </div>
            <div className="flex lg:hidden">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-foreground"
              >
                <span className="sr-only">Open main menu</span>
                <Menu className="size-6" />
              </button>
            </div>
            <div className="hidden lg:flex lg:gap-x-12">
              {navigation.map((item) => {
                const isActive = isActiveLink(item.href);
                return (
                  <RouterLink 
                    key={item.name} 
                    to={item.href} 
                    className={`text-sm font-semibold leading-6 transition-all duration-0 ${
                      isActive
                        ? 'text-transparent bg-gradient-to-r from-purple-600 to-orange-500 bg-clip-text underline'
                        : 'text-foreground hover:text-transparent hover:bg-gradient-to-r hover:from-purple-600 hover:to-orange-500 hover:bg-clip-text hover:underline'
                    }`}
                  >
                    {item.name}
                  </RouterLink>
                );
              })}
            </div>
            <div className="hidden lg:flex lg:flex-1 lg:justify-end gap-4 items-center">
              <RouterLink 
                to="/login" 
                className="text-sm font-semibold leading-6 text-foreground hover:text-transparent hover:bg-gradient-to-r hover:from-purple-600 hover:to-orange-500 hover:bg-clip-text hover:underline transition-all duration-0"
              >
                Log in
              </RouterLink>
              <Button size="sm" asChild className="group bg-gradient-to-r from-purple-600 to-orange-600 hover:to-orange-500 hover:from-purple-500  text-white transition-all duration-0">
                <RouterLink to="/signup">
                  Get started <ArrowRight className="ml-1 w-4 h-4 inline transition-transform group-hover:translate-x-0.5" />
                </RouterLink>
              </Button>
            </div>
          </nav>
          <Dialog open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <DialogContent>
              <div className="flex items-center justify-between">
                <RouterLink to="/" className="-m-1.5 p-1.5" onClick={() => setMobileMenuOpen(false)}>
                  <span className="sr-only">ChronoCal</span>
                  <img
                    alt="ChronoCal Logo"
                    src="/assets/icons/branding/chronocal-logo.svg"
                    className="h-6 w-auto"
                  />
                </RouterLink>
              </div>
              <div className="mt-6 flow-root">
                <div className="-my-6 divide-y divide-border">
                  <div className="space-y-2 py-6">
                    {navigation.map((item) => {
                      const isActive = isActiveLink(item.href);
                      return (
                        <RouterLink
                          key={item.name}
                          to={item.href}
                          className={`-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 transition-colors ${
                            isActive
                              ? 'bg-accent text-primary'
                              : 'text-foreground hover:bg-accent'
                          }`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {item.name}
                        </RouterLink>
                      );
                    })}
                    {/* Show help link only when user is logged in */}
                    {user && helpNavigation.map((item) => (
                      <RouterLink
                        key={item.name}
                        to={item.href}
                        className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-foreground hover:bg-accent"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </RouterLink>
                    ))}
                  </div>
                  <div className="py-6 space-y-3">
                    <RouterLink
                      to="/login"
                      className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-foreground hover:bg-accent"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Log in
                    </RouterLink>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        window.location.href = '/signup';
                      }}
                      className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-primary hover:bg-accent w-full text-left"
                    >
                      Sign Up
                    </button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </header>

        {/* Content with padding for the fixed header */}
        <div className="pt-16">
          {children}
        </div>
      </div>
    );
  }

  // For authenticated users on app pages, show the sidebar layout
  // Keep sidebar active during logout to prevent useSidebar errors
  // But don't show sidebar on public pages even if user is logged in
  if ((user || isLoggingOut) && !isPublicPage) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <AppSidebar />
          <div className={`flex-1 bg-gray-50 relative`}>
            <header className="sticky top-0 z-50 w-full border-b bg-white backdrop-blur supports-[backdrop-filter]:bg-background/60">
              <div className="flex flex-col w-full max-w-full">
                <div className="flex h-14 items-center px-4 justify-between w-full max-w-full">
                  <div className="flex items-center gap-1 min-w-0 flex-1">
                    <SidebarTrigger className="mr-2 flex-shrink-0" />
                    {/* <Separator orientation="vertical" className="mr-2 h-4 flex-shrink-0" /> */}
                    <Breadcrumbs overrides={breadcrumbOverrides} />
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    <HeaderTrialProgress />
                  </div>
                </div>
              </div>
            </header>
            <main className="flex-1">
              {children}
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  // Fallback: if we somehow get here without a user, show public navigation
  return (
    <div className="bg-background">
      <div className="pt-16">
        {children}
      </div>
    </div>
  );
}

// Export memoized component to prevent unnecessary re-renders
export const Navigation = React.memo(NavigationComponent);
